import { useState, type ReactNode } from 'react';
import { Button, Checkbox, Input, InputNumber, Radio, Select } from 'antd';
import { Link } from 'react-router';
import {
  ARRANGEMENT_LIMITS,
  TEMPLATE_SHIFTS_MAX,
  readableDate,
  recordTemplateDecisionSchema,
  templateDutyPointLabelOf,
  type PolicyListItemDto,
  type PostTypeDto,
  type RecordTemplateDecisionDto,
  type TemplateChoiceDto,
  type TemplateDayDto,
  type TemplateDecisionRowDto,
  type VehicleTypeDto,
} from '@pams/shared';
import { OrgUnitPicker } from '@/components/OrgUnitPicker';
import { parseTimeInput } from '@/lib/timeOfDay';
import { DECISIONS_COPY } from '../copy';
import {
  cellKey,
  cellsIn,
  columnSum,
  correctableColumns,
  createPolicyHref,
  createPostTypeHref,
  pointsWithColumn,
  printedRows,
  printedTotalsProblem,
} from './decisionRules';
import { policyCovers } from './useDecisionMasters';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B §3b] ONE FORM PER CLASS, EACH ANSWERING ITS CLASS'S ONE QUESTION — inline under its row,
 *  never a modal, so the overlay census does not move and the row's context stays on screen beside
 *  the choice.
 *
 *  ⚠️ EVERY BODY IS PARSED BY THE SHARED `recordTemplateDecisionSchema` BEFORE SAVE IS ENABLED — the
 *  same schema the server validates the PUT with, so a choice the server would refuse with a 422 is
 *  one the button never sends. What the schema cannot see is checked here too, and said: a printed
 *  total's corrected rows must SUM to the printed figure, column by column.
 *
 *  ⚠️ THE FORM STARTS FROM THE STORED DECISION, ELSE THE SUGGESTION — never blank where the server
 *  already has an answer, and never a suggestion silently saved: nothing is written until Save.
 *
 *  [amended P7C §4] "NEVER A MODAL" IS NOW TRUE OF THIS PAGE ONLY. The library's preview
 *  (`Library/DecisionModal.tsx`) renders this same form inside a dialog of its own — swept by the overlay
 *  census there — and saves through the page's own hook. The form itself renders no overlay, takes no
 *  route parameter and is unchanged: every prop it needs is handed in.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export type DecisionFormProps = {
  row: TemplateDecisionRowDto;
  setKey: string;
  days: readonly TemplateDayDto[];
  postTypes: readonly PostTypeDto[];
  vehicleTypes: readonly VehicleTypeDto[];
  policies: readonly PolicyListItemDto[];
  /** Units a `SOURCE` may not name — disabled in its picker, the server's own refusal stated early. */
  unallocatableUnitIds: readonly string[];
  /**
   * [P7B.1 §3b] May this officer create a post type — `postType.write`, a super admin. An OFFER gate: the
   * master's own route refuses everybody else whatever this says.
   */
  mayCreatePostType: boolean;
  saving: boolean;
  onSave: (body: RecordTemplateDecisionDto) => void;
  onCancel: () => void;
};

const startingChoice = (row: TemplateDecisionRowDto): TemplateChoiceDto | null =>
  row.decision?.choice ?? row.suggestion ?? null;

const Footer = ({
  body,
  problem,
  saving,
  onSave,
  onCancel,
}: {
  body: unknown;
  /** A rule the schema cannot see, worded — Save stays disabled while it is set. */
  problem?: string | undefined;
  saving: boolean;
  onSave: (body: RecordTemplateDecisionDto) => void;
  onCancel: () => void;
}): React.ReactElement => {
  const [note, setNote] = useState('');
  const withNote =
    typeof body === 'object' && body !== null && note.trim() !== '' ? { ...body, note: note.trim() } : body;
  const parsed = recordTemplateDecisionSchema.safeParse(withNote);
  return (
    <div className="flex flex-col gap-2">
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">{DECISIONS_COPY.note}</span>
        <Input value={note} maxLength={500} onChange={(event) => setNote(event.target.value)} />
      </label>
      {problem ? (
        <p className="text-xs text-muted-foreground" data-testid="decision-problem">
          {problem}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button
          type="primary"
          data-testid="decision-save"
          loading={saving}
          disabled={!parsed.success || problem !== undefined}
          onClick={() => {
            if (parsed.success && problem === undefined) onSave(parsed.data);
          }}
        >
          {DECISIONS_COPY.save}
        </Button>
        <Button onClick={onCancel}>{DECISIONS_COPY.cancel}</Button>
      </div>
    </div>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }): React.ReactElement => (
  <label className="flex max-w-md flex-col gap-1 text-sm">
    <span className="text-muted-foreground">{label}</span>
    {children}
  </label>
);

/**
 * A labelled GROUP of controls — never a `<label>`, which would nest each checkbox's own label inside it
 * and give every box the whole group's words as its accessible name.
 */
const FieldGroup = ({ label, children }: { label: string; children: ReactNode }): React.ReactElement => (
  <div role="group" aria-label={label} className="flex max-w-md flex-col gap-1 text-sm">
    <span className="text-muted-foreground">{label}</span>
    {children}
  </div>
);

// ── SOURCE ─────────────────────────────────────────────────────────────────────────

const SourceForm = ({ row, unallocatableUnitIds, saving, onSave, onCancel }: DecisionFormProps): React.ReactElement => {
  const start = startingChoice(row);
  const [mode, setMode] = useState<'ORG_UNIT' | 'SKIP'>(start?.choice === 'SKIP' ? 'SKIP' : 'ORG_UNIT');
  const [orgUnitId, setOrgUnitId] = useState<string | undefined>(
    start?.choice === 'ORG_UNIT' ? start.orgUnitId : undefined,
  );
  const body = {
    class: 'SOURCE',
    code: row.code,
    decision: mode === 'SKIP' ? { choice: 'SKIP' } : { choice: 'ORG_UNIT', orgUnitId },
  };
  return (
    <div className="flex flex-col gap-3">
      <Radio.Group value={mode} onChange={(event) => setMode(event.target.value as 'ORG_UNIT' | 'SKIP')}>
        <Radio value="ORG_UNIT">{DECISIONS_COPY.sourceOrgUnit}</Radio>
        <Radio value="SKIP">{DECISIONS_COPY.sourceSkip}</Radio>
      </Radio.Group>
      {mode === 'ORG_UNIT' ? (
        <Field label={DECISIONS_COPY.pickUnit}>
          <OrgUnitPicker
            value={orgUnitId}
            onChange={setOrgUnitId}
            defaultExpandAll={false}
            disabledIds={unallocatableUnitIds}
          />
        </Field>
      ) : null}
      <Footer body={body} saving={saving} onSave={onSave} onCancel={onCancel} />
    </div>
  );
};

// ── FORCE_COLUMN ───────────────────────────────────────────────────────────────────

type ColumnMode = 'POST_TYPE' | 'VEHICLE_TYPE' | 'ONE_OFF' | 'SKIP';

const ForceColumnForm = ({
  row,
  setKey,
  postTypes,
  vehicleTypes,
  mayCreatePostType,
  saving,
  onSave,
  onCancel,
}: DecisionFormProps): React.ReactElement => {
  const start = startingChoice(row);
  const [mode, setMode] = useState<ColumnMode>(
    start?.choice === 'VEHICLE_TYPE' || start?.choice === 'ONE_OFF' || start?.choice === 'SKIP'
      ? start.choice
      : 'POST_TYPE',
  );
  const [postTypeId, setPostTypeId] = useState<string | undefined>(
    start?.choice === 'POST_TYPE' ? start.postTypeId : undefined,
  );
  const [vehicleTypeId, setVehicleTypeId] = useState<string | undefined>(
    start?.choice === 'VEHICLE_TYPE' ? start.vehicleTypeId : undefined,
  );
  const oneOff = start?.choice === 'ONE_OFF' && 'restrictToPostTypeIds' in start ? start : null;
  const [label, setLabel] = useState(oneOff?.label ?? row.label);
  const [restrict, setRestrict] = useState<string[]>(oneOff ? [...oneOff.restrictToPostTypeIds] : []);
  const decision =
    mode === 'POST_TYPE'
      ? { choice: mode, postTypeId }
      : mode === 'VEHICLE_TYPE'
        ? { choice: mode, vehicleTypeId }
        : mode === 'ONE_OFF'
          ? { choice: mode, label, restrictToPostTypeIds: restrict }
          : { choice: mode };
  const postOptions = postTypes.map((row_) => ({ value: row_.id, label: row_.name }));
  return (
    <div className="flex flex-col gap-3">
      <Radio.Group value={mode} onChange={(event) => setMode(event.target.value as ColumnMode)}>
        <Radio value="POST_TYPE">{DECISIONS_COPY.columnPostType}</Radio>
        <Radio value="VEHICLE_TYPE">{DECISIONS_COPY.columnVehicleType}</Radio>
        <Radio value="ONE_OFF">{DECISIONS_COPY.columnOneOff}</Radio>
        <Radio value="SKIP">{DECISIONS_COPY.columnSkip}</Radio>
      </Radio.Group>
      {mode === 'POST_TYPE' ? (
        <>
          <Field label={DECISIONS_COPY.columnPostType}>
            <Select
              value={postTypeId}
              onChange={setPostTypeId}
              options={postOptions}
              showSearch
              optionFilterProp="label"
              data-testid="decision-post-type"
            />
          </Field>
          {/* [P7B.1 §3b] The column may need a post type nobody has made yet — offered, never made here. */}
          {mayCreatePostType ? (
            <Link
              to={createPostTypeHref(setKey, row.code)}
              className="text-sm text-link"
              data-testid="create-post-type"
            >
              {DECISIONS_COPY.createPostType}
            </Link>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="create-post-type-refused">
              {DECISIONS_COPY.createPostTypeRefused}
            </p>
          )}
        </>
      ) : null}
      {mode === 'VEHICLE_TYPE' ? (
        <Field label={DECISIONS_COPY.columnVehicleType}>
          <Select
            value={vehicleTypeId}
            onChange={setVehicleTypeId}
            options={vehicleTypes.map((row_) => ({ value: row_.id, label: row_.name }))}
            showSearch
            optionFilterProp="label"
          />
        </Field>
      ) : null}
      {mode === 'ONE_OFF' ? (
        <>
          <Field label={DECISIONS_COPY.oneOffLabel}>
            <Input value={label} maxLength={80} onChange={(event) => setLabel(event.target.value)} />
          </Field>
          <Field label={DECISIONS_COPY.restrictTo}>
            <Select
              mode="multiple"
              value={restrict}
              onChange={setRestrict}
              options={postOptions}
              optionFilterProp="label"
            />
          </Field>
        </>
      ) : null}
      <Footer
        body={{ class: 'FORCE_COLUMN', code: row.code, decision }}
        saving={saving}
        onSave={onSave}
        onCancel={onCancel}
      />
    </div>
  );
};

// ── POLICY ─────────────────────────────────────────────────────────────────────────

const PolicyForm = ({ row, setKey, policies, saving, onSave, onCancel }: DecisionFormProps): React.ReactElement => {
  const start = startingChoice(row);
  const [policyId, setPolicyId] = useState<string | undefined>(start?.choice === 'POLICY' ? start.policyId : undefined);
  const columns = row.context.class === 'POLICY' ? row.context.columns : [];
  return (
    <div className="flex flex-col gap-3">
      <Field label={DECISIONS_COPY.policy}>
        <Select
          value={policyId}
          onChange={setPolicyId}
          showSearch
          optionFilterProp="label"
          data-testid="decision-policy"
          options={policies.map((policy) => {
            const covers = policyCovers(policy, columns);
            return {
              value: policy.id,
              label: covers ? policy.name : `${policy.name} — ${DECISIONS_COPY.policyDoesNotCover}`,
              disabled: !covers,
            };
          })}
        />
      </Field>
      <Link to={createPolicyHref(setKey, row.label, columns)} className="text-sm text-link" data-testid="create-policy">
        {DECISIONS_COPY.createPolicy}
      </Link>
      <Footer
        body={{ class: 'POLICY', code: row.code, decision: { choice: 'POLICY', policyId } }}
        saving={saving}
        onSave={onSave}
        onCancel={onCancel}
      />
    </div>
  );
};

// ── IN_CHARGE_ROLE ─────────────────────────────────────────────────────────────────

type RoleMode = 'ROLE' | 'ONE_OFF' | 'DROP';

const InChargeRoleForm = ({ row, saving, onSave, onCancel }: DecisionFormProps): React.ReactElement => {
  const start = startingChoice(row);
  const context = row.context.class === 'IN_CHARGE_ROLE' ? row.context : null;
  const [mode, setMode] = useState<RoleMode>(
    start?.choice === 'ONE_OFF' || start?.choice === 'DROP' ? start.choice : 'ROLE',
  );
  const [mappedRole, setMappedRole] = useState<string | undefined>(
    start?.choice === 'ROLE' ? start.mappedRole : undefined,
  );
  const [label, setLabel] = useState(start?.choice === 'ONE_OFF' ? start.label : (context?.label ?? row.label));
  const decision =
    mode === 'ROLE' ? { choice: mode, mappedRole } : mode === 'ONE_OFF' ? { choice: mode, label } : { choice: mode };
  return (
    <div className="flex flex-col gap-3">
      <Radio.Group value={mode} onChange={(event) => setMode(event.target.value as RoleMode)}>
        <Radio value="ROLE">{DECISIONS_COPY.roleOnPolicy(context?.policyName ?? '')}</Radio>
        <Radio value="ONE_OFF">{DECISIONS_COPY.roleOneOff}</Radio>
        <Radio value="DROP">{DECISIONS_COPY.roleDrop}</Radio>
      </Radio.Group>
      {mode === 'ROLE' ? (
        <Field label={DECISIONS_COPY.roleOnPolicy(context?.policyName ?? '')}>
          <Select
            value={mappedRole}
            onChange={setMappedRole}
            options={(context?.roles ?? []).map((role) => ({ value: role.id, label: role.label }))}
            data-testid="decision-role"
          />
        </Field>
      ) : null}
      {mode === 'ONE_OFF' ? (
        <Field label={DECISIONS_COPY.roleLabel}>
          <Input value={label} maxLength={80} onChange={(event) => setLabel(event.target.value)} />
        </Field>
      ) : null}
      <Footer
        body={{ class: 'IN_CHARGE_ROLE', code: row.code, decision }}
        saving={saving}
        onSave={onSave}
        onCancel={onCancel}
      />
    </div>
  );
};

// ── TOTALS ─────────────────────────────────────────────────────────────────────────

type TotalsMode = 'AS_READ' | 'PRINTED' | 'HOLD';
/**
 * ⚠️ THE CORRECTIONS ARE ONLY THE CELLS A PERSON TYPED. A cell left alone is not sent, so the server
 * keeps it at its figure as read — and the sum shown is the server's own arithmetic (`columnSum`), the
 * in-charge count included, so Save is enabled exactly when the PUT would be accepted.
 */
const TotalsForm = ({ row, saving, onSave, onCancel }: DecisionFormProps): React.ReactElement => {
  const context = row.context.class === 'TOTALS' ? row.context : null;
  const start = startingChoice(row);
  const correctable = context ? correctableColumns(context) : [];
  const [mode, setMode] = useState<TotalsMode>(
    start?.choice === 'PRINTED' || start?.choice === 'HOLD' ? start.choice : 'AS_READ',
  );
  const [values, setValues] = useState<Record<string, number>>(() =>
    start?.choice === 'PRINTED'
      ? Object.fromEntries(start.rows.map((cell) => [cellKey(cell.dutyPointKey, cell.column), cell.n]))
      : {},
  );

  const decision =
    mode === 'PRINTED' && context ? { choice: mode, rows: printedRows(context, values) } : { choice: mode };
  const problem = mode === 'PRINTED' && context ? printedTotalsProblem(context, values) : undefined;

  return (
    <div className="flex flex-col gap-3">
      <Radio.Group value={mode} onChange={(event) => setMode(event.target.value as TotalsMode)}>
        <Radio value="AS_READ">{DECISIONS_COPY.totalsAsRead}</Radio>
        <Radio value="PRINTED" disabled={correctable.length === 0} data-testid="totals-printed">
          {DECISIONS_COPY.totalsPrinted}
        </Radio>
        <Radio value="HOLD">{DECISIONS_COPY.totalsHold}</Radio>
      </Radio.Group>
      {correctable.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="totals-uncorrectable">
          {DECISIONS_COPY.totalsUncorrectable}
        </p>
      ) : null}
      {mode === 'PRINTED' && context
        ? correctable.map(({ column, printed }) => {
            const sum = columnSum(context, column, values);
            const inCharge = context.columns.find((entry) => entry.column === column)?.inCharge ?? 0;
            return (
              <div key={column} className="flex flex-col gap-1 text-sm" data-testid={`totals-column-${column}`}>
                <span className="font-medium">{column}</span>
                <ul className="flex flex-col gap-1">
                  {pointsWithColumn(context, column).map((point) => {
                    const cells = cellsIn(point, column);
                    const key = cellKey(point.key, column);
                    const asRead = cells.map((cell) => String(cell.n)).join(' + ');
                    return (
                      <li key={point.key} className="flex flex-wrap items-center gap-2">
                        {/* [TEMPLATE-FIND] Named as a person reads the row — `(b)` or `table 1, row 3` — never its key. */}
                        <span className="min-w-48 text-muted-foreground">
                          {[templateDutyPointLabelOf(point), point.place]
                            .filter((part) => part !== null && part !== '')
                            .join(' · ')}
                        </span>
                        <InputNumber
                          min={0}
                          max={10_000}
                          precision={0}
                          value={values[key] ?? (cells.length === 1 ? cells[0]?.n : null)}
                          placeholder={asRead}
                          aria-label={`${column} at ${point.place ?? templateDutyPointLabelOf(point)}`}
                          onChange={(next) => {
                            setValues((current) => {
                              const { [key]: _dropped, ...rest } = current;
                              return next === null ? rest : { ...rest, [key]: Number(next) };
                            });
                          }}
                        />
                        {cells.length > 1 ? (
                          <span className="text-xs text-muted-foreground">
                            {DECISIONS_COPY.totalsEachRow(cells.length)}
                          </span>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
                <span
                  className={sum === printed ? 'text-muted-foreground' : 'text-foreground'}
                  data-testid={`totals-sum-${column}`}
                >
                  {DECISIONS_COPY.totalsSum(sum, printed)}
                  {inCharge > 0 ? ` (${DECISIONS_COPY.totalsInCharge(inCharge)})` : ''}
                </span>
              </div>
            );
          })
        : null}
      <Footer
        body={{ class: 'TOTALS', code: row.code, decision }}
        problem={problem}
        saving={saving}
        onSave={onSave}
        onCancel={onCancel}
      />
    </div>
  );
};

// ── SCHEDULE ───────────────────────────────────────────────────────────────────────

type ShiftText = { start: string; end: string };

const ScheduleForm = ({ row, days, saving, onSave, onCancel }: DecisionFormProps): React.ReactElement => {
  const start = startingChoice(row);
  const schedule = start?.choice === 'SCHEDULE' ? start.schedule : null;
  const [dayKeys, setDayKeys] = useState<string[]>(schedule ? [...schedule.dayKeys] : []);
  const [kind, setKind] = useState<'WHOLE_DAY' | 'SHIFTS'>(schedule?.timing.kind ?? 'WHOLE_DAY');
  const [reporting, setReporting] = useState(
    schedule?.timing.kind === 'WHOLE_DAY' ? (schedule.timing.reportingTime ?? '') : '',
  );
  const [shifts, setShifts] = useState<ShiftText[]>(
    schedule?.timing.kind === 'SHIFTS'
      ? schedule.timing.shifts.map((shift) => ({ ...shift }))
      : [{ start: '', end: '' }],
  );
  const [nextDay, setNextDay] = useState(schedule?.nextCalendarDay ?? false);
  const context = row.context.class === 'SCHEDULE' ? row.context : null;

  /* A typed time is read the way the PA intake reads one (`parseTimeInput`: `8` is 08:00). */
  const reportingTime = reporting.trim() === '' ? null : parseTimeInput(reporting);
  const parsedShifts = shifts.map((shift) => ({ start: parseTimeInput(shift.start), end: parseTimeInput(shift.end) }));
  const badTime =
    kind === 'WHOLE_DAY'
      ? reporting.trim() !== '' && reportingTime === null
      : parsedShifts.some((shift) => shift.start === null || shift.end === null);
  const timing =
    kind === 'WHOLE_DAY' ? { kind, reportingTime } : { kind, shifts: parsedShifts.map((shift) => ({ ...shift })) };
  // Keep the chosen days in the set's own order, so the stored decision reads in festival order.
  const orderedDays = days.filter((day) => dayKeys.includes(day.key)).map((day) => day.key);

  return (
    <div className="flex flex-col gap-3">
      {context ? (
        <div className="flex flex-col gap-0.5 text-sm" data-testid="schedule-source">
          <span className="text-muted-foreground">{DECISIONS_COPY.scheduleSource}</span>
          {context.sourceText.map((line) => (
            <span key={line} className="italic">
              {line}
            </span>
          ))}
        </div>
      ) : null}
      <FieldGroup label={DECISIONS_COPY.scheduleDays}>
        <Checkbox.Group
          value={dayKeys}
          onChange={(next) => setDayKeys(next.map(String))}
          options={days.map((day) => ({
            value: day.key,
            label: `${day.name} (${readableDate(day.sourceDate) ?? day.sourceDate})`,
          }))}
          className="flex flex-col gap-1"
        />
      </FieldGroup>
      <Radio.Group value={kind} onChange={(event) => setKind(event.target.value as 'WHOLE_DAY' | 'SHIFTS')}>
        <Radio value="WHOLE_DAY">{DECISIONS_COPY.scheduleWholeDay}</Radio>
        <Radio value="SHIFTS">{DECISIONS_COPY.scheduleShifts}</Radio>
      </Radio.Group>
      {kind === 'WHOLE_DAY' ? (
        <Field label={DECISIONS_COPY.scheduleReporting}>
          <Input
            value={reporting}
            placeholder={DECISIONS_COPY.timeHint}
            onChange={(event) => setReporting(event.target.value)}
            data-testid="schedule-reporting"
          />
        </Field>
      ) : (
        <ul className="flex flex-col gap-2">
          {shifts.map((shift, index) => (
            <li key={`shift-${String(index)}`} className="flex flex-wrap items-end gap-2">
              <span className="w-16 text-sm text-muted-foreground">{DECISIONS_COPY.scheduleShift(index)}</span>
              <Input
                className="w-24"
                value={shift.start}
                placeholder={DECISIONS_COPY.timeHint}
                aria-label={`${DECISIONS_COPY.scheduleShift(index)} ${DECISIONS_COPY.scheduleStart}`}
                onChange={(event) =>
                  setShifts((current) =>
                    current.map((each, at) => (at === index ? { ...each, start: event.target.value } : each)),
                  )
                }
              />
              <Input
                className="w-24"
                value={shift.end}
                placeholder={DECISIONS_COPY.timeHint}
                aria-label={`${DECISIONS_COPY.scheduleShift(index)} ${DECISIONS_COPY.scheduleEnd}`}
                onChange={(event) =>
                  setShifts((current) =>
                    current.map((each, at) => (at === index ? { ...each, end: event.target.value } : each)),
                  )
                }
              />
              {shifts.length > 1 ? (
                <Button type="text" onClick={() => setShifts((current) => current.filter((_each, at) => at !== index))}>
                  {DECISIONS_COPY.scheduleRemoveShift}
                </Button>
              ) : null}
            </li>
          ))}
          {shifts.length < TEMPLATE_SHIFTS_MAX ? (
            <li>
              <Button size="small" onClick={() => setShifts((current) => [...current, { start: '', end: '' }])}>
                {DECISIONS_COPY.scheduleAddShift}
              </Button>
            </li>
          ) : null}
        </ul>
      )}
      <Checkbox checked={nextDay} onChange={(event) => setNextDay(event.target.checked)}>
        {DECISIONS_COPY.scheduleNextDay}
      </Checkbox>
      <span className="text-xs text-muted-foreground">{DECISIONS_COPY.scheduleNextDayHint}</span>
      <Footer
        body={{
          class: 'SCHEDULE',
          code: row.code,
          decision: { choice: 'SCHEDULE', schedule: { dayKeys: orderedDays, timing, nextCalendarDay: nextDay } },
        }}
        problem={badTime ? DECISIONS_COPY.invalidTime : undefined}
        saving={saving}
        onSave={onSave}
        onCancel={onCancel}
      />
    </div>
  );
};

// ── DUTY_POINT ─────────────────────────────────────────────────────────────────────

const DutyPointForm = ({ row, saving, onSave, onCancel }: DecisionFormProps): React.ReactElement => {
  const context = row.context.class === 'DUTY_POINT' ? row.context : null;
  /* ⚠️ A duty point that asks for nobody cannot be rescued by a place — publish refuses it. Skip only. */
  const placeOffered = context?.issue !== 'NO_FORCE';
  const start = startingChoice(row);
  const [mode, setMode] = useState<'PLACE' | 'SKIP'>(!placeOffered || start?.choice === 'SKIP' ? 'SKIP' : 'PLACE');
  const [place, setPlace] = useState(start?.choice === 'PLACE' ? start.place : (context?.place ?? ''));
  return (
    <div className="flex flex-col gap-3">
      <Radio.Group value={mode} onChange={(event) => setMode(event.target.value as 'PLACE' | 'SKIP')}>
        {placeOffered ? <Radio value="PLACE">{DECISIONS_COPY.dpPlace}</Radio> : null}
        <Radio value="SKIP">{DECISIONS_COPY.dpSkip}</Radio>
      </Radio.Group>
      {mode === 'PLACE' ? (
        <Field label={DECISIONS_COPY.placeLabel}>
          <Input.TextArea
            value={place}
            maxLength={ARRANGEMENT_LIMITS.PLACE_NAME}
            showCount
            autoSize={{ minRows: 1, maxRows: 4 }}
            onChange={(event) => setPlace(event.target.value)}
            data-testid="decision-place"
          />
        </Field>
      ) : null}
      <Footer
        body={{
          class: 'DUTY_POINT',
          code: row.code,
          decision: mode === 'PLACE' ? { choice: 'PLACE', place } : { choice: 'SKIP' },
        }}
        /* [PLACE-500] A place over the limit (t156's 655) leaves Save disabled — say why, rather than grey it out. */
        problem={
          mode === 'PLACE' && place.trim().length > ARRANGEMENT_LIMITS.PLACE_NAME
            ? DECISIONS_COPY.placeTooLong
            : undefined
        }
        saving={saving}
        onSave={onSave}
        onCancel={onCancel}
      />
    </div>
  );
};

export const DecisionForm = (props: DecisionFormProps): React.ReactElement => {
  switch (props.row.class) {
    case 'SOURCE':
      return <SourceForm {...props} />;
    case 'FORCE_COLUMN':
      return <ForceColumnForm {...props} />;
    case 'POLICY':
      return <PolicyForm {...props} />;
    case 'IN_CHARGE_ROLE':
      return <InChargeRoleForm {...props} />;
    case 'TOTALS':
      return <TotalsForm {...props} />;
    case 'SCHEDULE':
      return <ScheduleForm {...props} />;
    case 'DUTY_POINT':
      return <DutyPointForm {...props} />;
  }
};
